import { z } from 'zod';

const envConfigSchema = z.object({
  env: z.enum(['DEV', 'STAGING', 'PROD']),
  apiGateway: z.object({
    REGION: z.string(),
    URL: z.string(),
  }),
  batchWebURL: z.string(),
  batchAdminURL: z.string(),
  cognito: z.object({
    REGION: z.string(),
    USER_POOL_ID: z.string(),
    APP_CLIENT_ID: z.string(),
    IDENTITY_POOL_ID: z.string(),
  }),
  shopifyLinks: z.object({
    order: z.string(),
    draftOrder: z.string(),
  }),
  stripe: z.object({
    publishableKey: z.string(),
    cardholderId: z.string(),
    dashboardUrl: z.string(),
  }),
  googleMapApiKey: z.string(),
  mixpanelToken: z.string(),
  fareharborDashboard: z.string(),
  growthBook: z.object({ apiHost: z.string(), clientKey: z.string() }),
});

type EnvConfig = z.infer<typeof envConfigSchema>;

const configs: EnvConfig[] = [
  {
    env: 'DEV' as const,
    apiGateway: {
      REGION: 'us-east-1',
      URL: 'https://xsj9r2npu7.execute-api.us-east-1.amazonaws.com/dev',
    },
    batchWebURL: 'https://qa.letsbatch.com',
    batchAdminURL: 'https://admin.qa.letsbatch.com',
    cognito: {
      REGION: 'us-east-1',
      USER_POOL_ID: 'us-east-1_DmaBMK5Yv',
      APP_CLIENT_ID: '382a81lmqcfjtjfr2g6h1p1inj',
      IDENTITY_POOL_ID: 'us-east-1:8e567f0f-4303-4408-8e0c-75a43a301723',
    },
    shopifyLinks: {
      order: 'https://bachpartydev.myshopify.com/admin',
      draftOrder: 'https://bachpartydev.myshopify.com/admin',
    },
    stripe: {
      publishableKey: 'pk_test_BVj9wlSnMVeQAyQAQgBZ0gwJ',
      cardholderId: 'ich_1KstJiB9g0biogx5wQrAazDF',
      dashboardUrl: 'https://dashboard.stripe.com/test',
    },
    googleMapApiKey: <string>process.env.REACT_APP_SECRETS_GOOGLE_MAP_API_KEY,
    mixpanelToken: '394d1eb0cd49be5d68ca2a4c6040c65c',
    fareharborDashboard: 'https://demo.fareharbor.com/bach/dashboard',
    growthBook: {
      apiHost: 'https://cdn.growthbook.io',
      clientKey: process.env.REACT_APP_SECRETS_GROWTHBOOK_CLIENT_KEY || '',
    },
  },
  {
    env: 'STAGING' as const,
    apiGateway: {
      REGION: 'us-east-1',
      URL: 'https://5wtkfg0019.execute-api.us-east-1.amazonaws.com/staging/',
    },
    batchWebURL: 'https://staging.letsbatch.com',
    batchAdminURL: 'https://admin.staging.letsbatch.com',
    cognito: {
      REGION: 'us-east-1',
      USER_POOL_ID: 'us-east-1_AsXK8SRS5',
      APP_CLIENT_ID: '1lqi4vrsogkiogens61d5oj9pl',
      IDENTITY_POOL_ID: 'us-east-1:35e876f7-eb53-478a-8c28-ab364453cb0b',
    },
    shopifyLinks: {
      order: 'https://bachparty.myshopify.com/admin',
      draftOrder: 'https://bachparty.myshopify.com/admin',
    },
    stripe: {
      publishableKey: 'pk_test_BVj9wlSnMVeQAyQAQgBZ0gwJ',
      cardholderId: 'ich_1KstJiB9g0biogx5wQrAazDF',
      dashboardUrl: 'https://dashboard.stripe.com/test',
    },
    googleMapApiKey: <string>process.env.REACT_APP_SECRETS_GOOGLE_MAP_API_KEY,
    mixpanelToken: '394d1eb0cd49be5d68ca2a4c6040c65c',
    fareharborDashboard: 'https://demo.fareharbor.com/bach/dashboard',
    growthBook: {
      apiHost: 'https://cdn.growthbook.io',
      clientKey: process.env.REACT_APP_SECRETS_GROWTHBOOK_CLIENT_KEY || '',
    },
  },
  {
    env: 'PROD' as const,
    apiGateway: {
      REGION: 'us-east-1',
      URL: 'https://c11cbkx9qa.execute-api.us-east-1.amazonaws.com/prod',
    },
    batchWebURL: 'https://letsbatch.com',
    batchAdminURL: 'https://admin.letsbatch.com',
    cognito: {
      REGION: 'us-east-1',
      USER_POOL_ID: 'us-east-1_sMkPDPT7Q',
      APP_CLIENT_ID: '5dehojvk1jqmpn1t7s04umjp96',
      IDENTITY_POOL_ID: 'us-east-1:769dfb1c-393f-4fca-9e95-97e74000294b',
    },
    shopifyLinks: {
      order: 'https://menna-tech.myshopify.com/admin',
      draftOrder: 'https://menna-tech.myshopify.com/admin',
    },
    stripe: {
      publishableKey: 'pk_live_wSROWLtQ6TTu4JHqFL441fJ5',
      cardholderId: 'ich_1KkzYpB9g0biogx5dFr6K8OI',
      dashboardUrl: 'https://dashboard.stripe.com',
    },
    googleMapApiKey: <string>process.env.REACT_APP_SECRETS_GOOGLE_MAP_API_KEY,
    mixpanelToken: 'aeb65fc0bda61363fa8958d554c9dcdc',
    fareharborDashboard: 'https://fareharbor.com/thebach/dashboard',
    growthBook: {
      apiHost: 'https://cdn.growthbook.io',
      clientKey: process.env.REACT_APP_SECRETS_GROWTHBOOK_CLIENT_KEY || '',
    },
  },
];

let appConfig: EnvConfig | undefined;
export const getConfig = () => {
  if (!appConfig) {
    const env = <string>process.env.REACT_APP_STAGE;
    appConfig = configs.find(config => config.env === env);
    if (!appConfig) {
      throw new Error(`Config for env ${env} not found`);
    }
  }

  return appConfig;
};
